<template>
    <v-form ref="form" v-model="valid" data-cy="payment-settings-account-form">
        <template v-if="multipleAccount">
            <v-row no-gutters class="mb-4">
                <v-spacer />
                <v-switch
                    v-model="credential.isActive"
                    hide-details
                    density="compact"
                    :label="credential.isActive ? $t('active') : $t('inactive')"
                    class="ml-auto"
                    data-cy="status"
                />
            </v-row>
            <v-row no-gutters>
                <v-col cols="12" md="4">
                    <v-text-field
                        v-model="credential.accountName"
                        :label="`${$t('name_of_account')}*`"
                        data-cy="name_of_account"
                        hide-details="auto"
                        :rules="[requiredRule('name_of_account')]"
                    />
                </v-col>
            </v-row>
            <div class="mb-4">
                <v-checkbox
                    v-model="credential.byDefault"
                    :label="$t('use_by_default')"
                    data-cy="is-default-account"
                    hide-details
                />
                <div class="text-medium-emphasis">({{ $t('default_account_note') }})</div>
            </div>
        </template>

        <div class="text-subtitle-1">{{ $t('base_currency_for_conversion') }}*</div>
        <v-select
            v-model="credential.baseCurrency"
            :items="provider.supportedCurrencies"
            :rules="[requiredRule('currency')]"
            data-cy="currency"
            hide-details
            max-width="150"
        />
        <div class="pb-4 text-medium-emphasis">{{ $t('base_currency_for_conversion_note') }}</div>
        <template v-if="runtimeStore.getCRMOption('asp.option.enable.multiple.accounts.for.payment.gateway')">
            <div class="text-subtitle-1 font-weight-medium pb-3">{{ $t('conditions') }}</div>
            <ConditionsSettings
                v-model="credential.conditions"
                :condition-value-map="conditionValueMap"
                :supported-currencies="provider.supportedCurrencies"
                class="pb-10"
            />
        </template>
        <v-row no-gutters>
            <v-col cols="12" lg="8">
                <div class="text-subtitle-1">
                    {{ $t('credentials') }}
                    <v-icon start color="primary" icon="mdi-information-outline" @click="isShowInfoModal = true" />
                </div>
                <ModalWrapper v-model="isShowInfoModal" max-width="800">
                    <template v-slot:title>{{ $t('credentials_help') }}</template>
                    <template v-slot:content>
                        <v-data-table
                            :headers="credentialsHeaders"
                            :items="provider.credentialsDescriptions"
                            disable-sort
                            density="compact"
                            class="elevation-1 mb-2"
                            :items-per-page="-1"
                            hide-default-footer
                            hide-default-header
                        />
                    </template>
                </ModalWrapper>
                <v-textarea v-model="credential.credential" data-cy="credentials" hide-details />
                <div v-if="!isValidated" class="text-error">
                    {{ $t('credential_warning', {prop: provider?.name}) }}
                </div>
            </v-col>
        </v-row>

        <template v-if="runtimeStore.getCRMOption('asp.option.enable.multiple.accounts.for.payment.gateway')">
            <div class="text-subtitle-1 pt-4 pb-3">{{ $t('availability') }}</div>
            <v-radio-group v-model="credential.availabilityType">
                <v-radio :label="$t('availabilityType.FOR_ALL')" value="FOR_ALL" data-cy="available_for_all" />
                <v-radio
                    :label="$t('availabilityType.CLIENT_TYPE')"
                    value="CLIENT_TYPE"
                    data-cy="for_certain_client_types"
                />
                <template v-if="credential.availabilityType === 'CLIENT_TYPE'">
                    <v-row no-gutters class="ga-2">
                        <v-selection-control-group
                            v-for="client in channelAvailability"
                            :key="client.value"
                            v-model="credential[client.value]"
                            :data-cy="client.value"
                        >
                            <v-checkbox :label="client.title" />
                        </v-selection-control-group>
                    </v-row>
                </template>
                <v-radio
                    :label="$t('availabilityType.SALES_CHANNEL')"
                    value="SALES_CHANNEL"
                    data-cy="for_selected_sales_channels"
                />
            </v-radio-group>
            <template v-if="credential.availabilityType === 'SALES_CHANNEL'">
                <SalesChannelManager
                    v-model="credential.salesChannels"
                    @update-direct-sales="credential.directSales = $event"
                />
            </template>
        </template>
    </v-form>
</template>
<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'

    defineProps<{
        multipleAccount?: boolean
        isValidated: boolean
        provider: any
    }>()

    const valid = ref<boolean>(false)
    const isShowInfoModal = ref<boolean>(false)
    const credential = defineModel<any>()
    const conditionValueMap = ref({})

    onMounted(async () => {
        if (Object.keys(supplierStore.pspSettingsConditions).length === 0)
            await supplierStore.loadPspSettingsConditions()
        conditionValueMap.value = supplierStore.pspSettingsConditions
    })

    const {t} = useI18n()

    const channelAvailability = computed(() => {
        const clients = ['availableForTO', 'availableForAgency', 'availableForCorporate']
        return clients.map(value => ({value, title: t(`accountAvailabilityType.${value}`)}))
    })

    const credentialsHeaders = computed(() => {
        return [
            {title: '', value: 'first'},
            {title: '', value: 'second', minWidth: '300', class: 'note-column'},
        ]
    })

    defineExpose({
        valid,
    })
</script>
